import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL, getAuthHeaders, feetToMeters, metersToFeet } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { useApiMessages } from "../hooks/useApiMessages";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { CheckpointMap } from "../components/Map/CheckpointMap";
import { MapContainer, Marker, Popup, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import GoogleTileLayer from "../components/Map/GoogleTileLayer";
import { MapPin, Plus, Pencil, Trash2, Crosshair, Locate, Maximize2, Minimize2, QrCode, RotateCcw, Printer, LayoutGrid, Table as TableIcon, GripVertical, ArrowUp, ArrowDown, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Component to handle map clicks in the form dialog
const FormMapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      if (onMapClick) {
        onMapClick(e.latlng);
      }
    },
  });
  return null;
};

// Component to center map on coordinates
const CenterMap = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center && Array.isArray(center) && center.length >= 2 && 
        !isNaN(center[0]) && !isNaN(center[1]) &&
        center[0] !== 0 && center[1] !== 0) {
      map.setView(center, zoom || 18, { animate: true });
    }
  }, [center, zoom, map]);
  return null;
};

export const Checkpoints = () => {
  const { t } = useTranslation();
  const { getErrorMessage, translateMessage } = useApiMessages();
  const { user, canManageCompanies } = useAuth();
  const [checkpoints, setCheckpoints] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [viewMode, setViewMode] = useState("tiles"); // "tiles" or "grid"
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isMapMaximized, setIsMapMaximized] = useState(false);
  const [isQrDialogOpen, setIsQrDialogOpen] = useState(false);
  const [selectedCheckpoint, setSelectedCheckpoint] = useState(null);
  const [resettingQr, setResettingQr] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    latitude: "",
    longitude: "",
    radius_feet: 50,
    company_id: "",
    description: "",
    instructions: [],
  });

  const fetchData = async (companyId = null) => {
    try {
      const params = new URLSearchParams();
      if (companyId && companyId !== "all") {
        params.append("company_id", companyId);
      }

      const [checkpointsRes, companiesRes] = await Promise.all([
        axios.get(`${API_URL}/checkpoints?${params.toString()}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/companies`, { headers: getAuthHeaders() }),
      ]);
      setCheckpoints(Array.isArray(checkpointsRes.data) ? checkpointsRes.data : []);
      setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(getErrorMessage(error, "api.errors.loadError"));
      setCheckpoints([]);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(selectedCompany);
  }, [selectedCompany]);

  const handleOpenForm = (checkpoint = null) => {
    if (checkpoint) {
      setSelectedCheckpoint(checkpoint);
      setFormData({
        name: checkpoint.name,
        latitude: checkpoint.latitude,
        longitude: checkpoint.longitude,
        radius_feet: checkpoint.radius_feet,
        company_id: checkpoint.company_id,
        description: checkpoint.description || "",
        instructions: checkpoint.instructions || [],
      });
    } else {
      setSelectedCheckpoint(null);
      setFormData({
        name: "",
        latitude: "",
        longitude: "",
        radius_feet: 50,
        company_id: canManageCompanies() ? "" : user.company_id,
        description: "",
        instructions: [],
      });
    }
    setIsFormOpen(true);
  };

  const handleMapClick = (latlng) => {
    if (isFormOpen) {
      setFormData({
        ...formData,
        latitude: latlng.lat.toFixed(6),
        longitude: latlng.lng.toFixed(6),
      });
      toast.info("Coordenadas actualizadas desde el mapa");
    }
  };

  const handleAddInstruction = () => {
    const newInstruction = {
      id: `temp-${Date.now()}`,
      text: "",
      requires_evidence: false,
      order: formData.instructions.length,
    };
    setFormData({
      ...formData,
      instructions: [...formData.instructions, newInstruction],
    });
  };

  const handleUpdateInstruction = (index, field, value) => {
    const updated = [...formData.instructions];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, instructions: updated });
  };

  const handleRemoveInstruction = (index) => {
    const updated = formData.instructions.filter((_, i) => i !== index);
    // Reordenar
    updated.forEach((inst, i) => {
      inst.order = i;
    });
    setFormData({ ...formData, instructions: updated });
  };

  const handleMoveInstruction = (index, direction) => {
    const updated = [...formData.instructions];
    if (direction === "up" && index > 0) {
      [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
      updated[index - 1].order = index - 1;
      updated[index].order = index;
    } else if (direction === "down" && index < updated.length - 1) {
      [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
      updated[index].order = index;
      updated[index + 1].order = index + 1;
    }
    setFormData({ ...formData, instructions: updated });
  };

  const handleGetCurrentLocation = async () => {
    if (!navigator.geolocation) {
      // Fallback: intentar obtener ubicación basada en IP
      toast.info(t("pages.checkpoints.geolocationUnavailable"));
      try {
        const response = await fetch("https://ipapi.co/json/");
        const data = await response.json();
        if (data.latitude && data.longitude) {
          const lat = parseFloat(data.latitude).toFixed(6);
          const lng = parseFloat(data.longitude).toFixed(6);
          setFormData({
            ...formData,
            latitude: lat,
            longitude: lng,
          });
          toast.success(t("pages.checkpoints.locationApproxIP", { lat, lng }));
          return;
        }
      } catch (ipError) {
        console.error("IP geolocation error:", ipError);
      }
      toast.error(t("pages.checkpoints.locationError"));
      return;
    }

    toast.info(t("pages.checkpoints.gettingLocation"));
    
    // Primero intentamos con alta precisión pero permitiendo caché
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude.toFixed(6);
        const lng = position.coords.longitude.toFixed(6);
        setFormData({
          ...formData,
          latitude: lat,
          longitude: lng,
        });
        toast.success(t("pages.checkpoints.locationObtained", { lat, lng }));
      },
      async (error) => {
        // Si falla con alta precisión, intentamos con configuración menos estricta
        if (error.code === error.TIMEOUT || error.code === error.POSITION_UNAVAILABLE) {
          toast.info(t("pages.checkpoints.retrying"));
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const lat = position.coords.latitude.toFixed(6);
              const lng = position.coords.longitude.toFixed(6);
              setFormData({
                ...formData,
                latitude: lat,
                longitude: lng,
              });
              toast.success(t("pages.checkpoints.locationObtained", { lat, lng }));
            },
            async (retryError) => {
              // Si falla también, intentamos con geolocalización por IP como fallback
              if (retryError.code === retryError.TIMEOUT || retryError.code === retryError.POSITION_UNAVAILABLE) {
                toast.info(t("pages.checkpoints.tryingIP"));
                try {
                  const response = await fetch("https://ipapi.co/json/");
                  const data = await response.json();
                  if (data.latitude && data.longitude) {
                    const lat = parseFloat(data.latitude).toFixed(6);
                    const lng = parseFloat(data.longitude).toFixed(6);
                    setFormData({
                      ...formData,
                      latitude: lat,
                      longitude: lng,
                    });
                    toast.success(t("pages.checkpoints.locationApproxIP", { lat, lng }));
                    return;
                  }
                } catch (ipError) {
                  console.error("IP geolocation error:", ipError);
                }
              }
              
              let errorMessage = t("pages.checkpoints.locationErrorGeneric");
              if (retryError.code === retryError.PERMISSION_DENIED) {
                errorMessage = t("pages.checkpoints.permissionDenied");
              } else if (retryError.code === retryError.POSITION_UNAVAILABLE) {
                errorMessage = t("pages.checkpoints.positionUnavailable");
              } else if (retryError.code === retryError.TIMEOUT) {
                errorMessage = t("pages.checkpoints.timeout");
              }
              toast.error(errorMessage);
              console.error("Geolocation error:", retryError);
            },
            { 
              enableHighAccuracy: false, // Menos preciso pero más rápido
              timeout: 30000, // 30 segundos
              maximumAge: 60000 // Permite usar ubicación en caché de hasta 1 minuto
            }
          );
        } else {
          let errorMessage = t("pages.checkpoints.locationErrorGeneric");
          if (error.code === error.PERMISSION_DENIED) {
            errorMessage = t("pages.checkpoints.permissionDenied");
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            errorMessage = t("pages.checkpoints.positionUnavailable");
          }
          toast.error(errorMessage);
          console.error("Geolocation error:", error);
        }
      },
      { 
        enableHighAccuracy: true,
        timeout: 20000, // Aumentado a 20 segundos
        maximumAge: 60000 // Permite usar ubicación en caché de hasta 1 minuto
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Filtrar instrucciones vacías y asegurar que tengan orden correcto
      const validInstructions = formData.instructions
        .filter((inst) => inst.text.trim() !== "")
        .map((inst, index) => ({
          ...inst,
          order: index,
        }));

      const data = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        radius_feet: parseFloat(formData.radius_feet),
        instructions: validInstructions,
      };

      if (selectedCheckpoint) {
        await axios.put(`${API_URL}/checkpoints/${selectedCheckpoint.id}`, data, {
          headers: getAuthHeaders(),
        });
        toast.success(t("api.success.checkpointUpdated"));
      } else {
        const response = await axios.post(`${API_URL}/checkpoints`, data, {
          headers: getAuthHeaders(),
        });
        toast.success(t("api.success.checkpointCreated"));
        // Resetear el formulario después de crear
        setFormData({
          name: "",
          latitude: "",
          longitude: "",
          radius_feet: 50,
          company_id: canManageCompanies() ? "" : user.company_id,
          description: "",
          instructions: [],
        });
      }
      setIsFormOpen(false);
      // Pequeño delay para asegurar que el backend haya procesado
      setTimeout(() => {
      fetchData(selectedCompany);
      }, 300);
    } catch (error) {
      toast.error(getErrorMessage(error, "api.errors.saveError"));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/checkpoints/${selectedCheckpoint.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success(t("api.success.checkpointDeleted"));
      setIsDeleteOpen(false);
      setSelectedCheckpoint(null);
      fetchData(selectedCompany);
    } catch (error) {
      toast.error(getErrorMessage(error, "api.errors.deleteError"));
    }
  };

  const handleResetQr = async () => {
    if (!selectedCheckpoint) return;
    setResettingQr(true);
    try {
      const response = await axios.post(
        `${API_URL}/checkpoints/${selectedCheckpoint.id}/reset-qr`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(t("api.success.qrReset"));
      // Actualizar el checkpoint seleccionado con el nuevo QR
      setSelectedCheckpoint(response.data);
      fetchData(selectedCompany);
    } catch (error) {
      toast.error(getErrorMessage(error, "api.errors.resetQRError"));
    } finally {
      setResettingQr(false);
    }
  };

  const handlePrintQr = () => {
    if (!selectedCheckpoint) return;
    
    // Usar API de QR code para generar imagen
    const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(selectedCheckpoint.qr_code)}`;
    
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>QR Code - ${selectedCheckpoint.name}</title>
            <style>
              @media print {
                @page {
                  margin: 20mm;
                }
              }
              body {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                margin: 0;
                font-family: Arial, sans-serif;
                padding: 20px;
              }
              h1 {
                margin-bottom: 20px;
                color: #333;
                font-size: 24px;
              }
              .qr-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 15px;
              }
              .qr-image {
                border: 2px solid #333;
                padding: 10px;
                background: white;
                width: 300px;
                height: 300px;
              }
              .checkpoint-name {
                font-size: 18px;
                font-weight: bold;
                margin-top: 10px;
                color: #333;
              }
            </style>
          </head>
          <body>
            <h1>{t("pages.checkpoints.qrCode")} - {t("nav.checkpoints")}</h1>
            <div class="qr-container">
              <img src="${qrApiUrl}" alt="QR Code" class="qr-image" onload="setTimeout(() => window.print(), 250)" />
              <div class="checkpoint-name">${selectedCheckpoint.name}</div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  return (
    <div className="space-y-6" data-testid="checkpoints-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            {t("pages.checkpoints.title")}
          </h1>
          <p className="text-slate-600 mt-1">
            {t("pages.checkpoints.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Filtro por compañía - Solo para admin y revendedor */}
          {canManageCompanies() && (
            <Select
              value={selectedCompany}
              onValueChange={(value) => {
                setSelectedCompany(value);
                setLoading(true);
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={t("pages.checkpoints.filterByCompany")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("pages.routes.allCompanies")}</SelectItem>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {/* Switch de vista */}
          <div className="flex items-center gap-2 px-3 py-2 border rounded-md">
            <LayoutGrid className={`h-4 w-4 ${viewMode === "tiles" ? "text-primary" : "text-slate-400"}`} />
            <Switch
              checked={viewMode === "grid"}
              onCheckedChange={(checked) => setViewMode(checked ? "grid" : "tiles")}
            />
            <TableIcon className={`h-4 w-4 ${viewMode === "grid" ? "text-primary" : "text-slate-400"}`} />
          </div>
          <Button onClick={() => handleOpenForm()} data-testid="add-checkpoint-btn">
            <Plus className="h-4 w-4 mr-2" />
            {t("pages.checkpoints.newCheckpoint")}
          </Button>
        </div>
      </div>

      {/* Map */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="h-5 w-5 text-primary" />
              {t("pages.checkpoints.mapTitle")}
              <span className="text-sm font-normal text-slate-500 ml-2">
                {t("pages.checkpoints.mapClickHint")}
              </span>
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsMapMaximized(true)}
              title={t("pages.checkpoints.maximizeMap")}
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <CheckpointMap
            checkpoints={checkpoints}
            selectedCheckpoint={selectedCheckpoint}
            onCheckpointClick={(cp) => {
              setSelectedCheckpoint(cp);
            }}
            onMapClick={handleMapClick}
            height="500px"
          />
        </CardContent>
      </Card>

      {/* Maximized Map Dialog */}
      <Dialog open={isMapMaximized} onOpenChange={setIsMapMaximized}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] p-0 translate-x-[-50%] translate-y-[-50%] flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2 text-lg">
                <MapPin className="h-5 w-5 text-primary" />
                {t("pages.checkpoints.mapTitle")}
                <span className="text-sm font-normal text-slate-500 ml-2">
                  {t("pages.checkpoints.mapClickHint")}
                </span>
              </DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsMapMaximized(false)}
                title="Minimizar mapa"
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          <div className="px-6 pb-6 flex-1 overflow-hidden min-h-0">
            <CheckpointMap
              checkpoints={checkpoints}
              selectedCheckpoint={selectedCheckpoint}
              onCheckpointClick={(cp) => {
                setSelectedCheckpoint(cp);
              }}
              onMapClick={handleMapClick}
              height="100%"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Checkpoint List */}
      <Card>
        <CardHeader>
          <CardTitle>{t("pages.checkpoints.listTitle", { count: Array.isArray(checkpoints) ? checkpoints.length : 0 })}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !Array.isArray(checkpoints) || checkpoints.length === 0 ? (
            <p className="text-slate-500 text-center py-8">
              {t("pages.checkpoints.noCheckpoints")}
            </p>
          ) : viewMode === "tiles" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {checkpoints.map((checkpoint, index) => (
                <div
                  key={checkpoint.id}
                  className={`p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                    selectedCheckpoint?.id === checkpoint.id
                      ? "border-orange-500 bg-orange-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  onClick={() => setSelectedCheckpoint(checkpoint)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{checkpoint.name}</p>
                        <p className="text-sm text-slate-500">{checkpoint.description}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1">
                    <p className="text-xs font-mono text-slate-500">
                      {checkpoint.latitude.toFixed(6)}, {checkpoint.longitude.toFixed(6)}
                    </p>
                    <p className="text-xs text-slate-500">
                      {t("pages.checkpoints.radius", { feet: checkpoint.radius_feet, meters: feetToMeters(checkpoint.radius_feet).toFixed(1) })}
                    </p>
                  </div>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenForm(checkpoint);
                      }}
                      data-testid={`edit-checkpoint-${checkpoint.id}`}
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      {t("pages.checkpoints.edit")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCheckpoint(checkpoint);
                        setIsQrDialogOpen(true);
                      }}
                      data-testid={`qr-checkpoint-${checkpoint.id}`}
                    >
                      <QrCode className="h-3 w-3 mr-1" />
                      {t("pages.checkpoints.qr")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCheckpoint(checkpoint);
                        setIsDeleteOpen(true);
                      }}
                      data-testid={`delete-checkpoint-${checkpoint.id}`}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      {t("pages.checkpoints.delete")}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>{t("common.name")}</TableHead>
                    <TableHead>{t("common.description")}</TableHead>
                    <TableHead>{t("pages.checkpoints.coordinates")}</TableHead>
                    <TableHead>{t("pages.checkpoints.radiusLabel")}</TableHead>
                    <TableHead className="text-right">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checkpoints.map((checkpoint, index) => (
                    <TableRow
                      key={checkpoint.id}
                      className={`cursor-pointer ${
                        selectedCheckpoint?.id === checkpoint.id
                          ? "bg-orange-50"
                          : ""
                      }`}
                      onClick={() => setSelectedCheckpoint(checkpoint)}
                    >
                      <TableCell>
                        <div className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center font-bold text-xs">
                          {index + 1}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{checkpoint.name}</TableCell>
                      <TableCell className="text-slate-500">{checkpoint.description || "-"}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {checkpoint.latitude.toFixed(6)}, {checkpoint.longitude.toFixed(6)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {t("pages.checkpoints.radius", { feet: checkpoint.radius_feet, meters: feetToMeters(checkpoint.radius_feet).toFixed(1) })}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenForm(checkpoint);
                            }}
                            data-testid={`edit-checkpoint-${checkpoint.id}`}
                          >
                            <Pencil className="h-3 w-3 mr-1" />
                            {t("pages.checkpoints.edit")}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCheckpoint(checkpoint);
                              setIsQrDialogOpen(true);
                            }}
                            data-testid={`qr-checkpoint-${checkpoint.id}`}
                          >
                            <QrCode className="h-3 w-3 mr-1" />
                            {t("pages.checkpoints.qr")}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCheckpoint(checkpoint);
                              setIsDeleteOpen(true);
                            }}
                            data-testid={`delete-checkpoint-${checkpoint.id}`}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            {t("pages.checkpoints.delete")}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedCheckpoint ? t("pages.checkpoints.editCheckpoint") : t("pages.checkpoints.newCheckpointTitle")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t("common.name")} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t("pages.checkpoints.namePlaceholder")}
                required
                data-testid="checkpoint-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t("common.description")}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t("common.description")}
                rows={2}
                data-testid="checkpoint-description-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t("pages.checkpoints.coordinates")} *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleGetCurrentLocation}
                  data-testid="get-current-location-btn"
                >
                  <Locate className="h-3 w-3 mr-1" />
                  {t("pages.checkpoints.useMyLocation")}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="latitude" className="text-xs text-slate-500">
                    {t("pages.checkpoints.latitude")}
                  </Label>
                  <Input
                    id="latitude"
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                    placeholder="18.478973"
                    required
                    className="font-mono"
                    data-testid="checkpoint-latitude-input"
                  />
                </div>
                <div>
                  <Label htmlFor="longitude" className="text-xs text-slate-500">
                    {t("pages.checkpoints.longitude")}
                  </Label>
                  <Input
                    id="longitude"
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                    placeholder="-69.967669"
                    required
                    className="font-mono"
                    data-testid="checkpoint-longitude-input"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">
                {t("pages.checkpoints.clickMapHint")}
              </p>
              
              {/* Interactive Map */}
              <div className="rounded-lg overflow-hidden border border-slate-200" style={{ height: "300px" }}>
                <MapContainer
                  center={
                    formData.latitude && formData.longitude && !isNaN(parseFloat(formData.latitude)) && !isNaN(parseFloat(formData.longitude))
                      ? [parseFloat(formData.latitude), parseFloat(formData.longitude)]
                      : [18.478973, -69.967669]
                  }
                  zoom={formData.latitude && formData.longitude && !isNaN(parseFloat(formData.latitude)) && !isNaN(parseFloat(formData.longitude)) ? 18 : 15}
                  style={{ height: "100%", width: "100%" }}
                  scrollWheelZoom={true}
                >
                  <GoogleTileLayer />
                  
                  <FormMapClickHandler
                    onMapClick={(latlng) => {
                      setFormData({
                        ...formData,
                        latitude: latlng.lat.toFixed(6),
                        longitude: latlng.lng.toFixed(6),
                      });
                      toast.info("Coordenadas actualizadas");
                    }}
                  />
                  
                  {/* Always render CenterMap to update when coordinates change */}
                  {formData.latitude && formData.longitude && 
                   !isNaN(parseFloat(formData.latitude)) && 
                   !isNaN(parseFloat(formData.longitude)) && (
                    <CenterMap
                      key={`center-${formData.latitude}-${formData.longitude}`}
                      center={[parseFloat(formData.latitude), parseFloat(formData.longitude)]}
                      zoom={18}
                    />
                  )}
                  
                  {/* Marker for selected location */}
                  {formData.latitude && formData.longitude && 
                   !isNaN(parseFloat(formData.latitude)) && 
                   !isNaN(parseFloat(formData.longitude)) && (
                    <Marker
                      key={`marker-${formData.latitude}-${formData.longitude}`}
                      position={[parseFloat(formData.latitude), parseFloat(formData.longitude)]}
                    >
                      <Popup>
                        <div className="p-2">
                          <p className="font-bold text-slate-900">{t("pages.checkpoints.selectedLocation")}</p>
                          <p className="text-xs font-mono text-slate-500">
                            {formData.latitude}, {formData.longitude}
                          </p>
                        </div>
                      </Popup>
                    </Marker>
                  )}
                </MapContainer>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="radius_feet">{t("pages.checkpoints.proximityRadius")} *</Label>
              <Input
                id="radius_feet"
                type="number"
                min="10"
                max="500"
                value={formData.radius_feet}
                onChange={(e) =>
                  setFormData({ ...formData, radius_feet: parseInt(e.target.value) || 50 })
                }
                required
                data-testid="checkpoint-radius-input"
              />
              <p className="text-xs text-slate-500">
                ≈ {feetToMeters(formData.radius_feet || 50).toFixed(1)} {t("pages.checkpoints.meters")}
              </p>
            </div>

            {/* Admin y revendedor ven selector de compañía */}
            {canManageCompanies() ? (
              <div className="space-y-2">
                <Label>{t("pages.companies.name")} *</Label>
                <Select
                  value={formData.company_id}
                  onValueChange={(value) => setFormData({ ...formData, company_id: value })}
                >
                  <SelectTrigger data-testid="checkpoint-company-select">
                    <SelectValue placeholder={t("pages.companies.selectCompany")} />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {/* Instrucciones */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <ListChecks className="h-4 w-4" />
                  {t("pages.checkpoints.instructions")}
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddInstruction}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {t("pages.checkpoints.addInstruction")}
                </Button>
              </div>
              
              {formData.instructions.length > 0 ? (
                <div className="space-y-2 border rounded-lg p-3 bg-slate-50">
                  {formData.instructions.map((instruction, index) => (
                    <div
                      key={instruction.id}
                      className="bg-white p-3 rounded border border-slate-200 space-y-2"
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex items-center gap-1 pt-2">
                          <GripVertical className="h-4 w-4 text-slate-400" />
                          <span className="text-xs text-slate-500 font-medium">
                            {index + 1}
                          </span>
                        </div>
                        <div className="flex-1 space-y-2">
                          <Input
                            placeholder={t("pages.checkpoints.instructionText")}
                            value={instruction.text}
                            onChange={(e) =>
                              handleUpdateInstruction(index, "text", e.target.value)
                            }
                            className="w-full"
                          />
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`requires-evidence-${index}`}
                              checked={instruction.requires_evidence}
                              onCheckedChange={(checked) =>
                                handleUpdateInstruction(
                                  index,
                                  "requires_evidence",
                                  checked
                                )
                              }
                            />
                            <Label
                              htmlFor={`requires-evidence-${index}`}
                              className="text-sm font-normal cursor-pointer"
                            >
                              {t("pages.checkpoints.requiresEvidence")}
                            </Label>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleMoveInstruction(index, "up")}
                            disabled={index === 0}
                          >
                            <ArrowUp className="h-3 w-3" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleMoveInstruction(index, "down")}
                            disabled={index === formData.instructions.length - 1}
                          >
                            <ArrowDown className="h-3 w-3" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveInstruction(index)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">
                  {t("pages.checkpoints.noInstructions")}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" data-testid="save-checkpoint-btn">
                {selectedCheckpoint ? t("common.saveChanges") : t("pages.checkpoints.createCheckpoint")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={isQrDialogOpen} onOpenChange={setIsQrDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("pages.checkpoints.qrCode")} - {selectedCheckpoint?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedCheckpoint?.qr_code && (
              <div className="flex flex-col items-center gap-4 p-4">
                <div className="bg-white p-4 rounded-lg border-2 border-slate-200">
                  <QRCodeSVG
                    value={selectedCheckpoint.qr_code}
                    size={256}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                <p className="text-sm text-slate-600 text-center">
                  Escanea este código QR desde la aplicación de patrullaje para registrar el punto de control
                </p>
                <div className="flex gap-2 w-full">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={handlePrintQr}
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Imprimir
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={handleResetQr}
                    disabled={resettingQr}
                  >
                    <RotateCcw className={`h-4 w-4 mr-2 ${resettingQr ? "animate-spin" : ""}`} />
                    Resetear QR
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.checkpoints.deleteCheckpoint")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("pages.checkpoints.deleteConfirm", { name: selectedCheckpoint?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-checkpoint"
            >
              {t("pages.checkpoints.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
